import { Avatar, Box, CircularProgress } from '@mui/material';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { vars } from '../../../Theme';
import { isValidUrl } from '../../../util/DataUtils';
import { stringToColor } from '../../../util/UiUtils';

/**
 * Component that shows the user's Auth0 profile image.
 */
export default (props: { size: number; loading?: boolean; userName?: string }) => {
    /** Access relevant state variables from the redux store. */
    const picture = useSelector((store: IAppReduxStore) => store.userProfile.data?.picture);
    const first_name = useSelector((store: IAppReduxStore) => store.userProfile.data?.first_name);
    const last_name = useSelector((store: IAppReduxStore) => store.userProfile.data?.last_name);
    const name = useSelector((store: IAppReduxStore) => store.userProfile.data?.name);

    /** Deconstruct props. */
    const { size, loading, userName } = props;

    /**
     * Provides properties for the User Avatar.
     * @returns Object of properties for the User Avatar.
     */
    const avatarProps = useCallback(
        (fullName: string) => ({
            border: `2px solid ${vars.white}`,
            bgcolor: stringToColor(fullName),
            borderRadius: '100%',
            height: size,
            width: size,
            fontSize: '12px',
        }),
        [size],
    );

    /**
     * Defines user's initials.
     * @returns User's initials.
     */
    const getAvatarsInitials = (firstName: string, lastName: string) =>
        firstName?.slice(0, 1).toUpperCase() + lastName?.slice(0, 1).toUpperCase();

    /** Handles loading prosess. */
    if (loading) {
        return (
            <Box sx={{ display: 'flex' }}>
                <CircularProgress />
            </Box>
        );
    }

    /** No user image or the user is different than the logged in user - display initials. */
    if (!picture || userName !== undefined) {
        const fullName = userName ?? name;

        const firstName = userName !== undefined ? userName.split(' ')[0] : first_name;
        const lastName =
            userName !== undefined
                ? /** assign the last item in substring as the lastName */
                  userName.split(' ')[userName.split(' ').length - 1]
                : last_name;

        return (
            firstName &&
            lastName && (
                <Avatar sx={{ ...avatarProps(fullName) }}>
                    {getAvatarsInitials(firstName, lastName)}
                </Avatar>
            )
        );
    }

    return (
        <Box
            sx={{
                backgroundImage: `url(${
                    isValidUrl(picture) ? '' : 'data:image/*;base64,'
                }${picture})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                ...avatarProps(name),
            }}
        />
    );
};
